import axios, {
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse
} from 'axios';
import { resolveApiRequestUrl } from './requestRouting';
import { reportApiTiming } from './rumReporter';
import { supabase } from './authService/supabase';
import { reportApiError } from './sentryReporter';

// 扩展 Window 接口
declare global {
  interface Window {
    Bustly: {
      workspace_id?: string | null;
    };
  }
}

export interface ApiConfig {
  baseURL: string;
  headers?: Record<string, string>;
  /** Optional runtime-owned token provider for hosts without browser Supabase storage. */
  getAccessToken?: () => Promise<string | null | undefined> | string | null | undefined;
  debugMode?: boolean;
}

export interface ApiResponse<T> {
    data: T;
    code: string;
    message: string;
    status: string;
    extra?: Record<string, unknown>;
}

const isNgrokUrl = (value?: string): boolean => {
    if (!value) {
        return false;
    }
    try {
        const url = new URL(value, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
        return url.hostname.endsWith('.ngrok-free.app') || url.hostname.endsWith('.ngrok.app');
    } catch {
        return false;
    }
};

const getHeaderValue = (headers: unknown, key: string): string | null => {
    if (!headers || typeof headers !== 'object') {
        return null;
    }

    const maybeAxiosHeaders = headers as { get?: (header: string) => unknown };
    if (typeof maybeAxiosHeaders.get === 'function') {
        const value = maybeAxiosHeaders.get(key);
        return value == null ? null : String(value);
    }

    const record = headers as Record<string, unknown>;
    const value = record[key] || record[key.toLowerCase()];
    return value == null ? null : String(value);
};

const setHeaderValue = (headers: unknown, key: string, value: string): void => {
    if (!headers || typeof headers !== 'object') {
        return;
    }

    const maybeAxiosHeaders = headers as { set?: (header: string, value: string) => void };
    if (typeof maybeAxiosHeaders.set === 'function') {
        maybeAxiosHeaders.set(key, value);
        return;
    }

    (headers as Record<string, string>)[key] = value;
};

const deleteHeaderValue = (headers: unknown, key: string): void => {
    if (!headers || typeof headers !== 'object') {
        return;
    }

    const maybeAxiosHeaders = headers as { delete?: (header: string) => void };
    if (typeof maybeAxiosHeaders.delete === 'function') {
        maybeAxiosHeaders.delete(key);
        return;
    }

    const record = headers as Record<string, unknown>;
    delete record[key];
    delete record[key.toLowerCase()];
};

export const SKIP_WORKSPACE_ID_HEADER = 'X-Skip-Workspace-Id';
export const SKIP_AUTH_HEADER = 'X-Skip-Auth';
const RUM_TIMING_REPORTED_FLAG = '__salerioRumTimingReported';

export class Api {
    private axiosInstance: AxiosInstance;

    constructor(apiConfig: ApiConfig) {
        this.axiosInstance = axios.create({
            baseURL: apiConfig.baseURL,
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json',
                ...apiConfig.headers
            },
        });

        // 请求拦截器
        this.axiosInstance.interceptors.request.use(
            async (axiosConfig) => {
                if (axiosConfig.url) {
                    axiosConfig.url = resolveApiRequestUrl(axiosConfig.url, {
                        baseURL: axiosConfig.baseURL || apiConfig.baseURL,
                    });
                }

                if (isNgrokUrl(axiosConfig.url)) {
                    axiosConfig.headers['ngrok-skip-browser-warning'] = 'true';
                }

                const skipAuthHeader = getHeaderValue(axiosConfig.headers, SKIP_AUTH_HEADER) === 'true';
                deleteHeaderValue(axiosConfig.headers, SKIP_AUTH_HEADER);
                if (!skipAuthHeader) {
                    const accessToken = apiConfig.getAccessToken
                        ? await apiConfig.getAccessToken()
                        : (await supabase.auth.getSession()).data.session?.access_token;
                    if (accessToken && !getHeaderValue(axiosConfig.headers, 'Authorization')) {
                        setHeaderValue(axiosConfig.headers, 'Authorization', `Bearer ${accessToken}`);
                    }
                }
                const skipWorkspaceIdHeader = getHeaderValue(
                    axiosConfig.headers,
                    SKIP_WORKSPACE_ID_HEADER
                ) === 'true';
                deleteHeaderValue(axiosConfig.headers, SKIP_WORKSPACE_ID_HEADER);

                // 优先使用传入的 header，如果没有则使用默认值
                if (
                    !skipWorkspaceIdHeader &&
                    !getHeaderValue(axiosConfig.headers, 'X-Workspace-Id') &&
                    typeof window !== 'undefined' &&
                    window.Bustly?.workspace_id
                ) {
                    setHeaderValue(axiosConfig.headers, 'X-Workspace-Id', window.Bustly.workspace_id);
                }

                return axiosConfig;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // 响应拦截器
        this.axiosInstance.interceptors.response.use(
            (response) => {
                return response;
            },
            (error) => {
                return Promise.reject(error);
            }
        );
    }

    private isCloudAgentConnectBundleRequest(config: AxiosRequestConfig): boolean {
        return typeof config.url === 'string' && config.url.includes('/api/v1/cloud-agent/connect-bundle');
    }

    // 通用请求方法
    private async request<T>(config: AxiosRequestConfig): Promise<T> {
        const startedAt = Date.now();
        try {
            if (this.isCloudAgentConnectBundleRequest(config)) {
                console.info('[cloud-agent] Api.request:start', {
                    method: config.method,
                    baseURL: this.axiosInstance.defaults.baseURL,
                    url: config.url,
                    timeout: this.axiosInstance.defaults.timeout,
                    workspaceHeader: config.headers?.['X-Workspace-Id'],
                });
            }
            const response: AxiosResponse<ApiResponse<T>> = await this.axiosInstance.request(config);
            if (this.isCloudAgentConnectBundleRequest(config)) {
                console.info('[cloud-agent] Api.request:response', {
                    status: response.status,
                    code: response.data?.code,
                    message: response.data?.message,
                    statusFlag: response.data?.status,
                    hasData: Boolean(response.data?.data),
                    data: response.data?.data,
                });
            }
            if(response.data.code && response.data.code !== '200') {
                const message = response.data.message || response.data.code;
                const apiError = new Error(message);
                (apiError as Error & { code?: string; response?: ApiResponse<T> }).code = response.data.code;
                (apiError as Error & { code?: string; response?: ApiResponse<T> }).response = response.data;
                (apiError as Error & Record<string, unknown>)[RUM_TIMING_REPORTED_FLAG] = true;
                reportApiTiming({
                    method: (response.config.method || config.method || 'GET').toUpperCase(),
                    route: String(response.config.url || config.url || ''),
                    status: response.status,
                    durationMs: Date.now() - startedAt,
                    timeout: false,
                    ok: false,
                });
                reportApiError(apiError, {
                    kind: 'business',
                    method: response.config.method || config.method,
                    url: response.config.url || config.url,
                    baseURL: response.config.baseURL || this.axiosInstance.defaults.baseURL,
                    status: response.status,
                    code: response.data.code,
                    message,
                    workspaceId: getHeaderValue(response.config.headers, 'X-Workspace-Id'),
                });
                throw apiError;
            }
            reportApiTiming({
                method: (response.config.method || config.method || 'GET').toUpperCase(),
                route: String(response.config.url || config.url || ''),
                status: response.status,
                durationMs: Date.now() - startedAt,
                timeout: false,
                ok: response.status < 400,
            });
            if (
                response.data
                && Object.prototype.hasOwnProperty.call(response.data, 'data')
            ) {
                return response.data.data as T;
            }
            return response.data as T;
        } catch (error) {
            if (this.isCloudAgentConnectBundleRequest(config)) {
                const axiosError = error as AxiosError<ApiResponse<T>>;
                console.error('[cloud-agent] Api.request:error', {
                    code: axiosError.code,
                    message: axiosError.message,
                    status: axiosError.response?.status,
                    responseData: axiosError.response?.data,
                    config: {
                        method: axiosError.config?.method,
                        baseURL: axiosError.config?.baseURL,
                        url: axiosError.config?.url,
                        timeout: axiosError.config?.timeout,
                    },
                });
            }
            const axiosError = error as AxiosError<ApiResponse<T>>;
            let normalizedError: unknown = error;
            const responseData = axiosError.response?.data;
            if (responseData?.code && responseData.code !== '200') {
                if (axios.isAxiosError(error)) {
                    const normalized = axiosError as AxiosError<ApiResponse<T>> & {
                        businessCode?: string;
                        apiResponse?: ApiResponse<T>;
                        httpStatus?: number;
                    };
                    normalized.message = responseData.message || responseData.code;
                    normalized.businessCode = responseData.code;
                    normalized.apiResponse = responseData;
                    normalized.httpStatus = axiosError.response?.status;
                    normalizedError = normalized;
                } else {
                    const apiError = new Error(responseData.message || responseData.code);
                    const normalized = apiError as Error & {
                        code?: string;
                        response?: ApiResponse<T>;
                        httpStatus?: number;
                        originalError?: unknown;
                    };
                    normalized.code = responseData.code;
                    normalized.response = responseData;
                    normalized.httpStatus = axiosError.response?.status;
                    normalized.originalError = error;
                    normalizedError = normalized;
                }
            }
            if (!(error && typeof error === 'object' && (error as Record<string, unknown>)[RUM_TIMING_REPORTED_FLAG])) {
                reportApiTiming({
                    method: (axiosError.config?.method || config.method || 'GET').toUpperCase(),
                    route: String(axiosError.config?.url || config.url || ''),
                    status: axiosError.response?.status ?? null,
                    durationMs: Date.now() - startedAt,
                    timeout: axiosError.code === 'ECONNABORTED' || axiosError.message?.toLowerCase().includes('timeout'),
                    ok: false,
                });
            }
            reportApiError(normalizedError, {
                kind: axiosError.response ? 'http' : 'request',
                method: axiosError.config?.method || config.method,
                url: axiosError.config?.url || config.url,
                baseURL: axiosError.config?.baseURL || this.axiosInstance.defaults.baseURL,
                status: axiosError.response?.status,
                code: axiosError.response?.data?.code || axiosError.code,
                message: axiosError.response?.data?.message || axiosError.message,
                workspaceId: getHeaderValue(axiosError.config?.headers || config.headers, 'X-Workspace-Id'),
            });
            throw normalizedError;
        }
    }

    // HTTP 方法
    public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({
            ...config, method: 'GET', url 
        });
    }

    public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({
            ...config, method: 'POST', url, data 
        });
    }

    public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({
            ...config, method: 'PUT', url, data 
        });
    }

    public async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({
            ...config, method: 'PATCH', url, data
        });
    }

    public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({
            ...config, method: 'DELETE', url 
        });
    }
}
